import { module as Module } from 'modujs';
import { createFocusTrap } from 'focus-trap';
import { delegateEvent } from '../utils/events';
import { EAGER_FONTS } from '../app';
import { whenReady } from '../utils/fonts';

export default class RvModal extends Module {
    static CLASS = {
        ACTIVE: 'is-active',
        GLOBAL_ACTIVE: 'has-rv-open'
    };

    static SELECTOR = '[data-rv-open]';

    constructor(m) {
        super(m);

        // UI
        this.$trap = this.$('trap')[0];
        this.$inner = this.$('inner')[0];
        this.$root = this.$('root')[0];
        this.$rootWrapper = this.$('rootWrapper')[0];
        this.$close = this.$('close')[0];
        this.$slider = this.$('slider')[0];
        this.$slides = Array.from(this.$('slide'));
        this.activeSlideIndex = 0;
        this.previousSlideIndex = -1;

        // Binding
        this.onOpen = this.onOpen.bind(this);
        this.onClose = this.onClose.bind(this);
        this.onActivate = this.onActivate.bind(this);
        this.onPostActivate = this.onPostActivate.bind(this);
        this.onDeactivate = this.onDeactivate.bind(this);
        this.onPostDeactivate = this.onPostDeactivate.bind(this);
        this.onNext = this.onNext.bind(this);
        this.onResizeEnd = this.onResizeEnd.bind(this);
        this.onFontsLoaded = this.onFontsLoaded.bind(this);
        this.onBreakPointChangeBind = this.onBreakPointChange.bind(this);

        // Data
        this.modalId = this.el.dataset.rvModalId;
        this.slideMetrics = [];
        this.breakpoint = window.matchMedia( `(pointer: fine)` )

        // Focus trap
        this.focusTrap = createFocusTrap(this.$trap, {
            onActivate: this.onActivate,
            onPostActivate: this.onPostActivate,
            onDeactivate: this.onDeactivate,
            onPostDeactivate: this.onPostDeactivate,
            clickOutsideDeactivates: true
        });
    }

    ///////////////
    // Lifecyle
    ///////////////
    init() {
        // Events
        this.bindEvents();
        whenReady(EAGER_FONTS).then((fonts) => this.onFontsLoaded(fonts));
        this.onBreakPointChange()
    }

    destroy() {
        super.destroy();
        this.unbindEvents();
    }

    ///////////////
    // Events
    ///////////////
    bindEvents() {
        this.unbindOpenEvent = delegateEvent(document.body, 'click', RvModal.SELECTOR, this.onOpen);
        this.$close?.addEventListener('click', this.onClose);
        window.addEventListener('resizeEnd', this.onResizeEnd);

        this.$slides.forEach(($slide, index) => {
            const $nextCta = this.$('nextCta')[index];
            $nextCta?.addEventListener('click', this.onNext);
        });

        if ('addEventListener' in this.breakpoint) {
            this.breakpoint.addEventListener('change', this.onBreakPointChangeBind )
        } else if ('addListener' in this.breakpoint) {
            this.breakpoint.addListener(this.onBreakPointChangeBind )
        }
    }

    unbindEvents() {
        this.unbindOpenEvent.abort?.();
        this.$close?.removeEventListener('click', this.onClose);
        window.removeEventListener('resizeEnd', this.onResizeEnd);

        this.$slides.forEach(($slide, index) => {
            const $nextCta = this.$('nextCta')[index];
            $nextCta?.removeEventListener('click', this.onNext);
        });

        if ('removeEventListener' in this.breakpoint) {
            this.breakpoint.addEventListener('change', this.onBreakPointChangeBind )
        } else if ('removeListener' in this.breakpoint) {
            this.breakpoint.addListener(this.onBreakPointChangeBind )
        }
    }

    ///////////////
    // Callbacks
    ///////////////
    onActivate() {
        this.el.setAttribute('aria-hidden', false);
    }

    onPostActivate() {
        this.$triggerCta?.setAttribute('aria-expanded', true);
    }

    onDeactivate() {
        this.el.classList.remove(RvModal.CLASS.ACTIVE);
        document.documentElement.classList.remove(RvModal.CLASS.GLOBAL_ACTIVE);
        this.el.setAttribute('aria-hidden', true);
        this.$slides[this.previousSlideIndex]?.classList.remove('is-prev');
        this.$slides[this.activeSlideIndex]?.classList.remove('is-active');
    }

    onPostDeactivate() {
        this.$triggerCta?.setAttribute('aria-expanded', false);
    }

    onOpen(e) {
        this.$triggerCta = e?.target;
        
        const modalId = this.$triggerCta?.dataset?.rvModalId;
        this.activeSlideIndex = Number(this.$triggerCta.dataset.index);
        this.previousSlideIndex = -1;

        if (modalId === this.modalId) {
            this.open();
            this.$slides[this.previousSlideIndex]?.classList.add('is-prev');
            this.scrollToNextSlide(true);
        }
    }

    onClose() {
        this.close();
    }

    onNext(e) {
        const $nextCta = e.target;
        const nextCtaIndex = this.$slides.indexOf($nextCta.closest('[data-rv-modal="slide"]'));
        this.$slides[this.previousSlideIndex]?.classList.remove('is-prev');
        this.$slides[this.activeSlideIndex]?.classList.remove('is-active');
        this.previousSlideIndex = this.activeSlideIndex;
        this.activeSlideIndex = (nextCtaIndex + 1) % this.$slides.length;
        this.scrollToNextSlide();
    }

    onResizeEnd() {
        this.setSlideMetrics();
    }

    onFontsLoaded() {
        this.setSlideMetrics();
    }

    onBreakPointChange() {
        if ( this.breakpoint.matches === true ) {
            this.isTouch = false;
        } else if ( this.breakpoint.matches === false ) {
            this.isTouch = true;
        }
    }

    ///////////////
    // Methods
    ///////////////
    open() {
        this.el.classList.add(RvModal.CLASS.ACTIVE);
        document.documentElement.classList.add(RvModal.CLASS.GLOBAL_ACTIVE);

        setTimeout(() => {
            this.focusTrap?.activate?.();
        }, 100);
    }

    close() {
        this.$triggerCta = null;
        this.focusTrap?.deactivate?.();
    }

    setSlideMetrics() {
        this.slideMetrics = this.$slides.map((slide, index) => {
            return {
                index,
                offset: slide.offsetWidth * index
            };
        });
    }

    scrollToNextSlide(instant = false) {
        if (this.isTouch) {
            const { offset } = this.slideMetrics[this.activeSlideIndex];
            this.$slider.scrollTo({ left: offset, behavior: instant ? 'auto' : 'smooth' });
        } else {
            this.$slides[this.previousSlideIndex]?.classList.add('is-prev');
            this.$slides[this.activeSlideIndex]?.classList.add('is-active');
            const $targetCard = this.$slides[this.activeSlideIndex].querySelector('[data-rv-modal="card"]')
            $targetCard.scrollTo({ top: 0, behavior: 'auto' });
        }
    }
}
